import React, {
    KeyboardEvent,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import AppContext from 'app/AppContext';

import './EstimatedTradeCostWidgetRequestQty.scss';
import {
    MessageIcon,
    NumericTextInput,
    ToolbarIcon,
} from '@instinet-analytics/semantic-ui-components';
import { TooltipWithoutContainer } from '@instinet-analytics/core-lib';
import { EstimatedTradeCostRequest } from 'widgets/estimatedTradeCost/store/data/EstimatedTradeCostRequest';
import useOnclickOutside from 'react-cool-onclickoutside';

export function EstimatedTradeCostWidgetRequestQty({
    editedQty,
    onQtyChanged,
    request,
    suppressLabel,
}: {
    editedQty: number | undefined;
    onQtyChanged: (qty: number) => void;
    suppressLabel?: boolean;
    request: EstimatedTradeCostRequest | undefined;
}) {
    const { numberFormat } = useContext(AppContext);

    const qty =
        editedQty ?? request?.specifiedQty ?? request?.orderQtyRemaining ?? 0;

    const [inputValue, setInputValue] = useState(qty);
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (editedQty === undefined) {
            setInputValue(
                request?.specifiedQty ?? request?.orderQtyRemaining ?? 0
            );
        }
    }, [editedQty, request?.specifiedQty, request?.orderQtyRemaining]);

    const onClickEdit = useCallback(() => setEditing(true), [setEditing]);

    const handleFinishEditing = useCallback(
        (save: boolean) => {
            if (save && inputValue !== qty) {
                onQtyChanged(inputValue);
            } else {
                setInputValue(qty);
            }
            setEditing(false);
        },
        [inputValue, qty, onQtyChanged, setInputValue, setEditing]
    );

    const ref = useOnclickOutside(() => {
        handleFinishEditing(true);
    });

    let toolTip = '';

    if (editedQty || request?.specifiedQty) {
        toolTip += `Updated quantity (order remaining quantity is ${numberFormat.n0(
            request?.orderQtyRemaining ?? 0
        )})`;
    } else if (request) {
        toolTip = `Order remaining quantity ${numberFormat.n0(
            request.orderQtyRemaining
        )}`;
    }

    const onQtyValueChanged = useCallback(
        (value: string) => {
            const i = parseInt(value);
            if (!isNaN(i)) {
                setInputValue(i);
            }
        },
        [setInputValue]
    );

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            handleFinishEditing(true);
        }
        if (e.key === 'Escape') {
            handleFinishEditing(false);
        }
    };

    return (
        <div className="EstimatedTradeCostWidgetRequestQty">
            <div className="DetailsPanelValue field">
                {!suppressLabel && (
                    <TooltipWithoutContainer
                        className="DetailsPanelValue_label"
                        text={toolTip}
                        tooltipVisible={false}
                    >
                        <div className="label">Quantity:</div>{' '}
                    </TooltipWithoutContainer>
                )}
                <div className="value DetailsPanelValue_value">
                    {!editing && (
                        <>
                            <div onClick={onClickEdit}>
                                {numberFormat.n0(qty)}
                            </div>
                            <div
                                className={'edit-qty-icon'}
                                onClick={onClickEdit}
                            >
                                <ToolbarIcon
                                    name={'edit'}
                                    tooltip={'Edit Quantity'}
                                />
                            </div>
                            {qty === 0 && (
                                <MessageIcon
                                    title={`The MAS Optimiser requires a non-zero quantity`}
                                />
                            )}
                        </>
                    )}
                    {editing && (
                        <div ref={ref}>
                            <NumericTextInput
                                className="edit-qty-input"
                                value={inputValue}
                                placeholder="Enter a quantity"
                                onChange={onQtyValueChanged}
                                autoFocus={true}
                                onKeyDown={onKeyDown}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
